// The reset page the "Forgot password?" mail opens (J-246, the owner's order item 4b): the token from
// the link, the new password twice, then sign-in with a status message. Better Auth keeps the
// 15-character minimum and the breach check, spends the token once, and ends every session of the
// account. The token and the passwords are never logged (T12 rule 1); the server drops the query
// string from its logs and spans, and the history entry that holds it is replaced on success.
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@vbltc/ui/button";
import { Input } from "@vbltc/ui/input";
import { StatusRegion } from "@vbltc/ui/components/status-region/status-region";
import { AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, AUTH_LABEL_CLASS, AUTH_LINK_CLASS } from "@vbltc/ui";
import { AuthPage } from "../app/auth-page";
import { authClient } from "../features/auth/client";
import { formString } from "../features/auth/form-value";
import { log } from "../telemetry";
import { resetPasswordSearchSchema } from "@/lib/route-search";

export const Route = createFileRoute("/reset-password")({
  validateSearch: resetPasswordSearchSchema,
  component: ResetPasswordPage,
});

const LINK_UNUSABLE = "This reset link has expired or was already used. Request a new link.";

/** Better Auth's refusal, in words. A refusal of the link itself offers a new one. */
function resetError(err: { status: number; code?: string | undefined }): {
  message: string;
  newLink: boolean;
} {
  if (err.code === "INVALID_TOKEN") return { message: LINK_UNUSABLE, newLink: true };
  if (err.code === "PASSWORD_TOO_SHORT")
    return { message: "Use at least 15 characters.", newLink: false };
  if (err.code === "PASSWORD_TOO_LONG")
    return { message: "Use at most 128 characters.", newLink: false };
  if (err.code === "PASSWORD_COMPROMISED")
    return {
      message: "This password appears in a known data breach. Choose a different one.",
      newLink: false,
    };
  if (err.status === 429)
    return { message: "Too many attempts. Wait a few minutes, then try again.", newLink: false };
  return { message: "The password could not be changed. Try again.", newLink: false };
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const [error, setError] = useState<{ message: string; newLink: boolean } | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (token === undefined) return;
    setError(null);
    const form = new FormData(e.currentTarget);
    const newPassword = formString(form, "newPassword");
    if (newPassword !== formString(form, "confirmPassword")) {
      setError({ message: "The two passwords do not match.", newLink: false });
      return;
    }
    setPending(true);
    const { error: err } = await authClient.resetPassword({ newPassword, token });
    setPending(false);
    if (err) {
      log.debug("password reset rejected", err.status); // status only; never the token or password
      setError(resetError(err));
      return;
    }
    // `replace`: the entry that held the token leaves the history.
    void navigate({
      to: "/login",
      search: { reason: "password_reset", redirect: undefined, minutes: undefined },
      replace: true,
    });
  }

  if (token === undefined)
    return (
      <AuthPage heading="Choose a new password">
        <StatusRegion
          tone="moderate"
          message="This reset link is incomplete. Request a new link."
          className="mb-4.5 rounded-md px-4"
        />
        <Link to="/forgot-password" className={AUTH_LINK_CLASS}>
          Request a new link
        </Link>
      </AuthPage>
    );

  return (
    <AuthPage
      heading="Choose a new password"
      description="Use at least 15 characters. Signing in again is needed on every device."
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        aria-label="Choose a new password"
        className="flex flex-col gap-4.5"
      >
        <label className={AUTH_LABEL_CLASS}>
          New password
          <Input
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            minLength={15}
            maxLength={128}
            className={AUTH_INPUT_CLASS}
          />
        </label>
        <label className={AUTH_LABEL_CLASS}>
          Confirm new password
          <Input
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            minLength={15}
            maxLength={128}
            className={AUTH_INPUT_CLASS}
          />
        </label>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error.message}
          </p>
        )}
        <Button
          type="submit"
          disabled={pending}
          data-telemetry="reset-password-submit"
          className={AUTH_BUTTON_CLASS}
        >
          Set new password
        </Button>
        {error?.newLink ? (
          <Link to="/forgot-password" className={AUTH_LINK_CLASS}>
            Request a new link
          </Link>
        ) : (
          <Link to="/login" className={AUTH_LINK_CLASS} data-telemetry="reset-password-back">
            Back to sign in
          </Link>
        )}
      </form>
    </AuthPage>
  );
}
